/**
 * ascension.ts — the second prestige layer. Ascending banks "Cores" earned from
 * accumulated prestige progress and resets BOTH the prestige layer and the run,
 * while carrying ascension/achievements/settings/rng/daily. It does NOT call
 * doPrestige (that would double-reset / double-count); it builds its own carry
 * set and does a single Object.assign — exactly mirroring prestige.ts's approach.
 */
import { newGame, type GameState } from '../core/GameState';
import type { EventBus } from '../core/EventBus';
import { BALANCE, ASCENSION_PERK_BY_ID } from '../data';

/** Cores the player would earn by ascending right now. */
export function ascensionGain(state: GameState): number {
  const { Kc, scaleC, exp, minPrestigePoints } = BALANCE.ascension;
  if (state.prestige.totalEarned < minPrestigePoints) return 0;
  return Math.floor(Kc * Math.pow(state.prestige.totalEarned / scaleC, exp));
}

export function canAscend(state: GameState): boolean {
  return ascensionGain(state) > 0;
}

/**
 * Perform an ascension: bank cores and reset everything except the carried set.
 */
export function doAscend(state: GameState, now: number, bus?: EventBus): boolean {
  const gain = ascensionGain(state);
  if (gain <= 0) return false;

  const carry = {
    cores: state.ascension.cores + gain,
    totalCores: state.ascension.totalCores + gain,
    ascensions: state.ascension.ascensions + 1,
    perks: state.ascension.perks,
  };
  const achievements = state.achievements;
  const settings = state.settings;
  const daily = state.daily;
  const createdAt = state.meta.createdAt;
  const playtimeMs = state.meta.playtimeMs;
  const rng = state.rng;

  const fresh = newGame(now);
  fresh.ascension = carry;
  fresh.achievements = achievements;
  fresh.settings = settings;
  fresh.daily = daily;
  fresh.meta.createdAt = createdAt;
  fresh.meta.playtimeMs = playtimeMs;
  fresh.rng = rng;

  Object.assign(state, fresh);

  bus?.emit('ascend', { cores: gain, total: carry.totalCores });
  bus?.emit('notify', { kind: 'reward', message: `🌌 Aufgestiegen für ${gain} Kerne!` });
  return true;
}

// --- Perk tree ---
export function perkCost(state: GameState, id: string): number {
  const p = ASCENSION_PERK_BY_ID[id];
  if (!p) return Infinity;
  const lvl = state.ascension.perks[id] ?? 0;
  return Math.ceil(p.baseCost * Math.pow(p.costGrowth, lvl));
}
export function perkUnlocked(state: GameState, id: string): boolean {
  const p = ASCENSION_PERK_BY_ID[id];
  if (!p) return false;
  if (!p.requires?.perk) return true;
  const [reqId, reqLvl] = p.requires.perk;
  return (state.ascension.perks[reqId] ?? 0) >= reqLvl;
}
export function canBuyPerk(state: GameState, id: string): boolean {
  const p = ASCENSION_PERK_BY_ID[id];
  if (!p || !perkUnlocked(state, id)) return false;
  const lvl = state.ascension.perks[id] ?? 0;
  if (lvl >= p.maxLevel) return false;
  return state.ascension.cores >= perkCost(state, id);
}
export function buyPerk(state: GameState, id: string, bus?: EventBus): boolean {
  if (!canBuyPerk(state, id)) return false;
  state.ascension.cores -= perkCost(state, id);
  state.ascension.perks[id] = (state.ascension.perks[id] ?? 0) + 1;
  bus?.emit('notify', { kind: 'success', message: `${ASCENSION_PERK_BY_ID[id]!.name} verbessert` });
  return true;
}
